import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { FiCheck, FiCopy, FiEye, FiEyeOff, FiLink, FiLoader, FiMail, FiRefreshCw, FiSend, FiX } from "react-icons/fi";
import config from "../../config";
import Labels from "../../services/apiLabels";
import { UPDATE_AUTH } from "../../constant";
import {
  creditAmountTone,
  formatActivityDate,
  formatCreditAmount,
  loadAccountActivity,
  pickLatestEvaluationDate,
  resolveLastEvaluation,
} from "../../utils/accountActivity";
import {
  buildAuthProfilePatch,
  buildReferralUrl,
  formatAuthDate,
  getAuthAvatarUrl,
  getAuthCredits,
  getAuthDisplayName,
  getAuthInitials,
  getAuthLastEvaluation,
  getAuthReferralCode,
  getAuthToken,
  getAuthUserId,
  pickUserImage,
  resolveMediaUrl,
  withCacheBust,
} from "../../utils/userIdentity";
import { isDealerUser, pickUserType } from "../../utils/userTypes";
import {
  BUSINESS_TYPE_OPTIONS,
  NEXT_GEAR_OPTIONS,
  EMPTY_CLIENT_PROFILE_FORM,
  EMPTY_DEALER_PROFILE_FORM,
  buildClientProfileFields,
  buildDealerProfileFields,
  buildProfileBody,
  mapUserToClientProfileForm,
  mapUserToDealerProfileForm,
  pickProfileLocationLists,
} from "../../utils/profileForms";
import CountrySelect from "../CountrySelect";
import PhoneWithCountryCode from "../PhoneWithCountryCode";
import {
  isCountryEnabled,
  resolveDialForCountryName,
} from "../../utils/countries";
import {
  REFERRAL_COPY,
  sendReferralInvite,
} from "../../utils/sendReferral";
import "./AccountQuickModal.css";

function emptyProfileForm(isDealer) {
  return isDealer
    ? { ...EMPTY_DEALER_PROFILE_FORM }
    : { ...EMPTY_CLIENT_PROFILE_FORM };
}

function isProfileUpdateError(data) {
  if (data?.message?.type === "error") return true;
  const imageErr = data?.data?.image;
  return Array.isArray(imageErr) && imageErr.length > 0;
}

function profileUpdateErrorMessage(data, fallback) {
  const imageErr = data?.data?.image;
  if (Array.isArray(imageErr) && imageErr[0]) return String(imageErr[0]);
  if (typeof imageErr === "string" && imageErr) return imageErr;
  return (
    data?.message?.description ||
    data?.message?.heading ||
    fallback
  );
}

function pickImageFromResponse(data) {
  const candidates = [
    data?.data?.user?.image,
    data?.data?.user?.profile_image,
    data?.user?.image,
    data?.user?.profile_image,
    data?.data?.image,
    data?.data?.profile_image,
    data?.image,
    data?.profile_image,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const value = candidates[i];
    if (typeof value === "string" && value && !Array.isArray(value)) {
      return value;
    }
  }
  return "";
}

const TITLES = {
  profile: {
    eyebrow: "Account",
    title: "Edit profile",
    lead: "Update your contact details for valuations and appointments.",
  },
  credits: {
    eyebrow: "Wallet",
    title: "Credits & evaluations",
    lead: "Balance, credit history, and every evaluation with its current status.",
  },
  referral: {
    eyebrow: "Invite",
    title: "Invite friends",
    lead: "Copy your personal signup link, or send it by email. Credits go to both wallets when they sign up.",
  },
  password: {
    eyebrow: "Security",
    title: "Change password",
    lead: "Update your login password. You stay signed in after saving.",
  },
};

function AccountQuickModal({ open, panel, onClose, auth, dispatch }) {
  const history = useHistory();
  const isDealer = isDealerUser(auth);
  // Keep last panel while Bootstrap runs its close animation — otherwise
  // `panel || "profile"` from the parent flashes the wide profile dialog.
  const activePanelRef = useRef(panel || "profile");
  if (open && panel) {
    activePanelRef.current = panel;
  }
  const activePanel = open && panel ? panel : activePanelRef.current;
  const meta =
    activePanel === "profile"
      ? {
          eyebrow: "Account",
          title: "Edit profile",
          lead: isDealer
            ? "Update your dealership and trade-floor contact details."
            : "Update your contact details for valuations and appointments.",
        }
      : TITLES[activePanel] || TITLES.profile;
  const userId = getAuthUserId(auth);
  const token = getAuthToken(auth);
  const referralUrl = useMemo(() => buildReferralUrl(auth), [auth]);
  const authCredits = useMemo(() => getAuthCredits(auth), [auth]);
  const authLastEvaluation = useMemo(() => getAuthLastEvaluation(auth), [auth]);

  const [profileForm, setProfileForm] = useState(() =>
    emptyProfileForm(isDealerUser(auth))
  );
  const [locationLists, setLocationLists] = useState({
    county: [],
    country: [],
  });
  const [identity, setIdentity] = useState({ username: "", email: "" });
  const [imagePreview, setImagePreview] = useState("");
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const profileFormRef = useRef(emptyProfileForm(isDealerUser(auth)));
  const fileInputRef = useRef(null);
  const [passwordForm, setPasswordForm] = useState({
    password: "",
    newpassword: "",
    confirmpassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    password: false,
    newpassword: false,
    confirmpassword: false,
  });
  const [credits, setCredits] = useState(authCredits);
  const [lastEvaluation, setLastEvaluation] = useState(authLastEvaluation);
  const [creditHistory, setCreditHistory] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [historyNote, setHistoryNote] = useState("");
  const [evaluationsNote, setEvaluationsNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [copied, setCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");

  useEffect(() => {
    profileFormRef.current = profileForm;
  }, [profileForm]);

  useEffect(() => {
    if (!open) return;
    setMessage(null);
    setCopied(false);
    setInviteEmail("");
    setSaving(false);
    setImageUploading(false);
    setAvatarFailed(false);
    setPasswordForm({ password: "", newpassword: "", confirmpassword: "" });
    setShowPassword({
      password: false,
      newpassword: false,
      confirmpassword: false,
    });
    setCredits(authCredits);
    setLastEvaluation(authLastEvaluation);
    setCreditHistory([]);
    setEvaluations([]);
    setHistoryNote("");
    setEvaluationsNote("");
    // Always seed identity/form from the signed-in session immediately
    setIdentity({
      username: auth?.username || auth?.user?.username || "",
      email: auth?.email || auth?.user?.email || "",
    });
    if (panel === "profile") {
      const seeded = isDealer
        ? mapUserToDealerProfileForm(auth)
        : mapUserToClientProfileForm(auth);
      setProfileForm(seeded);
      profileFormRef.current = seeded;
      const fromAuth = getAuthAvatarUrl(auth);
      if (fromAuth) {
        setImagePreview(withCacheBust(fromAuth));
      }
    }
    if (!userId && token) {
      setMessage({
        type: "err",
        text: "Your session is missing account details. Please log out and log in again.",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset UI when opening/switching panels
  }, [open, panel]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarGap =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarGap > 0) {
      document.body.style.paddingRight = `${scrollbarGap}px`;
    }
    document.body.classList.add("pi-account-modal-open");

    const isEditableTarget = (target) => {
      if (!target || !(target instanceof Element)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      return Boolean(
        target.closest("input, textarea, select, [contenteditable='true']")
      );
    };

    const blockScrollKeys = (event) => {
      const scrollKeys = [
        "ArrowUp",
        "ArrowDown",
        "PageUp",
        "PageDown",
        "Home",
        "End",
        " ",
        "Spacebar",
      ];
      if (!scrollKeys.includes(event.key)) return;
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
    };

    const blockOutsideScroll = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(".pi-account-modal__scroll")) return;
      event.preventDefault();
    };

    window.addEventListener("keydown", blockScrollKeys, { capture: true });
    document.addEventListener("wheel", blockOutsideScroll, {
      passive: false,
      capture: true,
    });
    document.addEventListener("touchmove", blockOutsideScroll, {
      passive: false,
      capture: true,
    });

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      document.body.classList.remove("pi-account-modal-open");
      window.removeEventListener("keydown", blockScrollKeys, { capture: true });
      document.removeEventListener("wheel", blockOutsideScroll, {
        capture: true,
      });
      document.removeEventListener("touchmove", blockOutsideScroll, {
        capture: true,
      });
    };
  }, [open]);

  // Keep Available credits in sync when balance updates after debit steps.
  useEffect(() => {
    if (!open || panel !== "credits") return;
    if (authCredits != null) setCredits(authCredits);
    if (authLastEvaluation) setLastEvaluation(authLastEvaluation);
  }, [open, panel, authCredits, authLastEvaluation]);

  useEffect(() => {
    setAvatarFailed(false);
  }, [imagePreview]);

  const refreshCreditsPanel = () => {
    if (!token || !userId) return;
    setLoading(true);
    // Available credits = sum of all ledger transactions (not stale profile).
    loadAccountActivity({ userId, token, creditPageSize: 5 })
      .then((activity) => {
        if (activity.credits != null) setCredits(activity.credits);
        const nextLast = resolveLastEvaluation(
          activity.lastEvaluation || authLastEvaluation,
          activity.evaluations || []
        );
        if (nextLast) setLastEvaluation(nextLast);
        setCreditHistory(activity.creditHistory || []);
        setEvaluations(activity.evaluations || []);
        setHistoryNote(activity.creditHistoryError || "");
        setEvaluationsNote(activity.evaluationsError || "");
        const payload = {};
        if (activity.credits != null) payload.credits = activity.credits;
        if (nextLast) payload.last_evaluation = nextLast;
        if (Object.keys(payload).length) {
          dispatch({ type: UPDATE_AUTH, payload });
        }
      })
      .catch(() => {
        setHistoryNote("Could not refresh balance from profile.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !token || !userId) return undefined;
    if (panel !== "profile" && panel !== "credits") return undefined;

    let cancelled = false;
    setLoading(true);

    if (panel === "credits") {
      loadAccountActivity({ userId, token, creditPageSize: 5 })
        .then((activity) => {
          if (cancelled) return;
          if (activity.credits != null) setCredits(activity.credits);
          const nextLast = resolveLastEvaluation(
            activity.lastEvaluation || authLastEvaluation,
            activity.evaluations || []
          );
          if (nextLast) setLastEvaluation(nextLast);
          setCreditHistory(activity.creditHistory || []);
          setEvaluations(activity.evaluations || []);
          setHistoryNote(activity.creditHistoryError || "");
          setEvaluationsNote(activity.evaluationsError || "");
          const payload = {};
          if (activity.credits != null) payload.credits = activity.credits;
          if (nextLast) payload.last_evaluation = nextLast;
          if (Object.keys(payload).length) {
            dispatch({ type: UPDATE_AUTH, payload });
          }
        })
        .catch(() => {
          if (cancelled) return;
          setHistoryNote("Could not load credit history.");
          setEvaluationsNote("Could not load evaluations.");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });

      return () => {
        cancelled = true;
      };
    }

    axios
      .get(`${config.baseUrl}${Labels.getupdateprofile}${userId}`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((response) => {
        if (cancelled) return;
        const payload = response?.data || {};
        const user =
          payload?.data?.user ||
          payload?.user ||
          payload?.data ||
          payload ||
          {};

        const nextForm = isDealerUser(auth)
          ? mapUserToDealerProfileForm(user)
          : mapUserToClientProfileForm(user);
        setProfileForm(nextForm);
        profileFormRef.current = nextForm;
        setLocationLists(pickProfileLocationLists(payload));
        setIdentity({
          username: user.username || auth.username || "",
          email: user.email || auth.email || "",
        });
        const img =
          pickUserImage(user, payload?.data, payload) ||
          getAuthAvatarUrl(auth);
        const raw =
          user.image ||
          user.image_url ||
          user.profile_image ||
          user.profile_image_url ||
          user.avatar ||
          "";
        const resolvedType = pickUserType(user, payload);
        const referralCode = user.referral_code || user.code || "";
        dispatch({
          type: UPDATE_AUTH,
          payload: {
            ...buildAuthProfilePatch(nextForm, raw || undefined),
            ...(resolvedType != null ? { type: resolvedType } : {}),
            ...(user.email ? { email: user.email } : {}),
            ...(user.username ? { username: user.username } : {}),
            ...(referralCode
              ? { referral_code: referralCode, code: referralCode }
              : {}),
          },
        });
        if (img) {
          setImagePreview(withCacheBust(resolveMediaUrl(img) || img));
          setAvatarFailed(false);
        }
        // If API omitted image, keep existing preview / auth avatar.
      })
      .catch(() => {
        if (cancelled) return;
        const nextForm = isDealerUser(auth)
          ? mapUserToDealerProfileForm(auth)
          : mapUserToClientProfileForm(auth);
        setProfileForm(nextForm);
        profileFormRef.current = nextForm;
        setIdentity({
          username: auth.username || "",
          email: auth.email || "",
        });
        const fromAuth = getAuthAvatarUrl(auth);
        if (fromAuth) {
          setImagePreview(withCacheBust(fromAuth));
        }
        setMessage({
          type: "err",
          text: "Could not load full profile. Showing signed-in details.",
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, panel, token, userId, dispatch]);

  const onProfileChange = (event) => {
    const { name, value } = event.target;
    setProfileForm((prev) => {
      const next = { ...prev, [name]: value };
      profileFormRef.current = next;
      return next;
    });
  };

  const onCountryChange = (countryName) => {
    setProfileForm((prev) => {
      const next = {
        ...prev,
        country: countryName,
        phone_dial: resolveDialForCountryName(countryName, prev.phone_dial),
      };
      profileFormRef.current = next;
      return next;
    });
  };

  const onPhoneDialChange = (dial, dialCountry) => {
    setProfileForm((prev) => {
      const next = {
        ...prev,
        phone_dial: dial,
        ...(dialCountry?.name && isCountryEnabled(dialCountry.code)
          ? { country: dialCountry.name }
          : {}),
      };
      profileFormRef.current = next;
      return next;
    });
  };

  const onPhoneNationalChange = (national) => {
    setProfileForm((prev) => {
      const next = { ...prev, phone_national: national };
      profileFormRef.current = next;
      return next;
    });
  };

  const syncAvatarToAuth = (imagePath) => {
    if (!imagePath) return "";
    dispatch({
      type: UPDATE_AUTH,
      payload: buildAuthProfilePatch(profileFormRef.current, imagePath),
    });
    const preview = withCacheBust(resolveMediaUrl(imagePath));
    setImagePreview(preview);
    setAvatarFailed(false);
    return preview;
  };

  const syncProfileFieldsToAuth = (imagePath) => {
    const form = profileFormRef.current || {};
    const typeFromForm = isDealer
      ? Number(form.business_type) || pickUserType(auth)
      : pickUserType(auth) ?? 6;
    dispatch({
      type: UPDATE_AUTH,
      payload: {
        ...buildAuthProfilePatch(form, imagePath || undefined),
        ...(typeFromForm != null ? { type: typeFromForm } : {}),
      },
    });
  };

  const fetchSavedAvatar = async () => {
    const response = await axios.get(
      `${config.baseUrl}${Labels.getupdateprofile}${userId}`,
      { headers: { Authorization: `Token ${token}` } }
    );
    const user =
      response?.data?.data?.user ||
      response?.data?.user ||
      response?.data?.data ||
      response?.data ||
      {};
    return user.image || user.profile_image || "";
  };

  const putClientProfile = async (imageValue, successText) => {
    if (!userId || !token) return null;
    const url = `${config.baseUrl}${Labels.getupdateprofile}${userId}/`;
    const fields = isDealer
      ? buildDealerProfileFields(profileFormRef.current)
      : buildClientProfileFields(profileFormRef.current);
    const payload = buildProfileBody(fields, imageValue);

    let response;
    let data;
    if (payload instanceof FormData) {
      // Do not set Content-Type — browser must add multipart boundary.
      response = await fetch(url, {
        method: "PUT",
        headers: { Authorization: `Token ${token}` },
        body: payload,
      });
      data = await response.json().catch(() => ({}));
    } else {
      response = await axios.put(url, payload, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      data = response?.data;
    }

    if (
      (payload instanceof FormData && !response.ok) ||
      isProfileUpdateError(data)
    ) {
      throw new Error(
        profileUpdateErrorMessage(data, "Profile update failed.")
      );
    }

    if (successText) {
      setMessage({ type: "ok", text: successText });
    }
    return data;
  };

  const onPickImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !userId || !token) return;
    const previousPreview = imagePreview;
    setImagePreview(URL.createObjectURL(file));
    setImageUploading(true);
    setMessage(null);
    try {
      const data = await putClientProfile(file, "Profile photo updated.");
      let saved = pickImageFromResponse(data);
      if (!saved) {
        saved = await fetchSavedAvatar();
      }
      if (saved) {
        syncAvatarToAuth(saved);
      } else {
        setImagePreview(previousPreview || getAuthAvatarUrl(auth) || "");
      }
    } catch (e) {
      setImagePreview(previousPreview || getAuthAvatarUrl(auth) || "");
      setMessage({
        type: "err",
        text:
          e?.message ||
          "Could not update profile photo. Please try again.",
      });
    } finally {
      setImageUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    if (!userId || !token) return;
    const previousPreview = imagePreview;
    setSaving(true);
    setMessage(null);
    try {
      const data = await putClientProfile(
        undefined,
        "Profile updated successfully."
      );
      const saved = pickImageFromResponse(data);
      if (saved) {
        syncAvatarToAuth(saved);
      } else {
        // Info-only update — keep current photo in edit UI and header.
        const fromAuth = getAuthAvatarUrl(auth);
        setImagePreview(
          previousPreview || (fromAuth ? withCacheBust(fromAuth) : "")
        );
        setAvatarFailed(false);
        syncProfileFieldsToAuth();
      }
    } catch (e) {
      setImagePreview(previousPreview || getAuthAvatarUrl(auth) || "");
      setMessage({
        type: "err",
        text: "Could not update profile. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    setMessage(null);
    if (passwordForm.newpassword.length < 8) {
      setMessage({
        type: "err",
        text: "New password must be at least 8 characters.",
      });
      return;
    }
    if (passwordForm.newpassword !== passwordForm.confirmpassword) {
      setMessage({
        type: "err",
        text: "New password and confirmation do not match.",
      });
      return;
    }
    if (!token) {
      setMessage({ type: "err", text: "Please log in again." });
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${config.baseUrl}${Labels.changePassword}${userId}/`,
        {
          old_password: passwordForm.password,
          new_password: passwordForm.newpassword,
        },
        { headers: { Authorization: `Token ${token}` } }
      );
      setPasswordForm({ password: "", newpassword: "", confirmpassword: "" });
      setMessage({ type: "ok", text: "Password updated successfully." });
    } catch (e) {
      setMessage({
        type: "err",
        text: "Could not change password. Check your current password and try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const copyReferralUrl = async () => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setMessage({
        type: "ok",
        text: getAuthReferralCode(auth)
          ? REFERRAL_COPY.copied
          : REFERRAL_COPY.copiedNoCode,
      });
      window.setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setCopied(false);
      setMessage({
        type: "err",
        text: "Could not copy the link. Select it and copy manually.",
      });
    }
  };

  const sendReferralEmail = async (event) => {
    event.preventDefault();
    setMessage(null);
    setSaving(true);
    try {
      const result = await sendReferralInvite({ email: inviteEmail, token });
      setInviteEmail("");
      setMessage({ type: "ok", text: result.message });
    } catch (error) {
      setMessage({
        type: "err",
        text: error?.message || REFERRAL_COPY.emailFailed,
      });
    } finally {
      setSaving(false);
    }
  };

  const renderBody = () => {
    if (loading && activePanel === "profile") {
      return (
        <p className="pi-account-modal__note">
          Loading… <Spinner animation="border" size="sm" />
        </p>
      );
    }

    if (activePanel === "credits") {
      const openEvaluation = () => {
        onClose?.();
        history.push("/myappointment");
      };

      const lastEvalDate =
        formatAuthDate(lastEvaluation) ||
        formatAuthDate(pickLatestEvaluationDate(evaluations)) ||
        "";

      return (
        <>
          <div className="pi-account-modal__stats">
            <div className="pi-account-modal__stat">
              <span>Available credits</span>
              <strong>{credits == null ? "—" : credits}</strong>
            </div>
            <div className="pi-account-modal__stat">
              <span>Last evaluation</span>
              <strong>{lastEvalDate || "—"}</strong>
            </div>
          </div>
          <button
            type="button"
            className="pi-account-modal__link-btn"
            onClick={refreshCreditsPanel}
            disabled={loading}
            style={{ marginBottom: "0.75rem" }}
          >
            <FiRefreshCw size={14} aria-hidden style={{ marginRight: 6 }} />
            {loading ? "Refreshing…" : "Refresh balance"}
          </button>

          <section className="pi-account-modal__section">
            <div className="pi-account-modal__section-head">
              <h3>Credit history</h3>
              <span>
                {loading && !creditHistory.length
                  ? "…"
                  : `${creditHistory.length} entries`}
              </span>
            </div>
            {loading && !creditHistory.length ? (
              <p className="pi-account-modal__note">
                Loading history… <Spinner animation="border" size="sm" />
              </p>
            ) : creditHistory.length ? (
              <ul className="pi-account-modal__list">
                {creditHistory.slice(0, 2).map((row) => (
                  <li key={row.id} className="pi-account-modal__list-item">
                    <div>
                      <strong>{row.title}</strong>
                      <p>
                        {[
                          formatActivityDate(row.date),
                          row.vehicle,
                          row.status,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                    <em
                      className={`pi-account-modal__amount pi-account-modal__amount--${creditAmountTone(
                        row.amount
                      )}`}
                    >
                      {formatCreditAmount(row.amount)}
                    </em>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="pi-account-modal__note">
                {historyNote ||
                  "No credit history is available on this account yet."}
              </p>
            )}
            <button
              type="button"
              className="pi-account-modal__link-btn"
              onClick={() => {
                onClose?.();
                history.push("/credits");
              }}
            >
              View full credit history
            </button>
          </section>

          <section className="pi-account-modal__section">
            <div className="pi-account-modal__section-head">
              <h3>Evaluations</h3>
              <span>
                {loading && !evaluations.length
                  ? "…"
                  : `${evaluations.length} total`}
              </span>
            </div>
            {loading && !evaluations.length ? (
              <p className="pi-account-modal__note">
                Loading evaluations… <Spinner animation="border" size="sm" />
              </p>
            ) : evaluations.length ? (
              <ul className="pi-account-modal__list">
                {evaluations.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className="pi-account-modal__list-item pi-account-modal__list-item--btn"
                      onClick={openEvaluation}
                    >
                      <div>
                        <strong>{item.title}</strong>
                        <p>
                          {[item.reg, formatActivityDate(item.date), item.tracking]
                            .filter(Boolean)
                            .join(" · ") || "Open details"}
                        </p>
                      </div>
                      <span
                        className={`pi-account-modal__badge pi-account-modal__badge--${item.statusTone}`}
                      >
                        {item.statusLabel}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="pi-account-modal__note">
                {evaluationsNote ||
                  "No evaluations found yet. Start a valuation to see them here."}
              </p>
            )}
            {evaluations.length ? (
              <button
                type="button"
                className="pi-account-modal__link-btn"
                onClick={() => {
                  onClose?.();
                  history.push("/myappointment");
                }}
              >
                View all appointments
              </button>
            ) : null}
          </section>
        </>
      );
    }

    if (activePanel === "referral") {
      const code = getAuthReferralCode(auth);
      return (
        <div className="pi-account-modal__referral">
          <p className="pi-account-modal__note">
            Signed in as <strong>{getAuthDisplayName(auth)}</strong>
            {code ? (
              <>
                {" "}
                · code <strong>{code}</strong>
              </>
            ) : (
              <> — no referral code on this account yet.</>
            )}
          </p>

          <section className="pi-referral-card">
            <div className="pi-referral-card__icon" aria-hidden>
              <FiLink size={18} />
            </div>
            <div className="pi-referral-card__body">
              <h2>Your referral link</h2>
              <p>
                {code ? REFERRAL_COPY.linkWithCode : REFERRAL_COPY.linkWithoutCode}
              </p>
              {!code ? (
                <p className="pi-account-modal__note">{REFERRAL_COPY.noCode}</p>
              ) : null}
              <div className="pi-account-modal__copy-row">
                <input readOnly value={referralUrl} aria-label="Referral URL" />
                <button
                  type="button"
                  onClick={copyReferralUrl}
                  disabled={!referralUrl}
                  aria-label={copied ? "Copied" : "Copy referral URL"}
                  title={copied ? "Copied" : "Copy"}
                  className="pi-account-modal__copy-btn"
                >
                  {copied ? <FiCheck size={18} aria-hidden /> : <FiCopy size={18} aria-hidden />}
                </button>
              </div>
            </div>
          </section>

          <section className="pi-referral-card pi-referral-card--invite">
            <div className="pi-referral-card__icon" aria-hidden>
              <FiMail size={18} />
            </div>
            <div className="pi-referral-card__body">
              <h2>Send by email</h2>
              <p>{REFERRAL_COPY.emailHint}</p>
              <form className="pi-account-modal__copy-row" onSubmit={sendReferralEmail}>
                <input
                  id="account-referral-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="name@email.com"
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  aria-label="Friend’s email"
                  required
                  disabled={saving}
                />
                <button
                  type="submit"
                  className="pi-account-modal__copy-btn"
                  disabled={saving || !inviteEmail.trim()}
                  aria-label={saving ? "Sending" : "Send invite"}
                  title={saving ? "Sending…" : "Send"}
                >
                  {saving ? (
                    <FiLoader size={18} className="pi-spin" aria-hidden />
                  ) : (
                    <FiSend size={18} aria-hidden />
                  )}
                </button>
              </form>
            </div>
          </section>
        </div>
      );
    }

    if (activePanel === "password") {
      const toggleVisibility = (field) => {
        setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
      };

      return (
        <form className="pi-account-modal__form" onSubmit={savePassword}>
          <div className="pi-account-modal__field">
            <span id="current-password-label">Current password</span>
            <span className="pi-account-modal__control">
              <input
                type={showPassword.password ? "text" : "password"}
                name="password"
                autoComplete="current-password"
                value={passwordForm.password}
                onChange={(e) =>
                  setPasswordForm((prev) => ({ ...prev, password: e.target.value }))
                }
                aria-labelledby="current-password-label"
                required
              />
              <button
                type="button"
                className="pi-account-modal__toggle-password"
                onClick={() => toggleVisibility("password")}
                aria-label={
                  showPassword.password ? "Hide current password" : "Show current password"
                }
                aria-pressed={showPassword.password}
              >
                {showPassword.password ? <FiEyeOff size={17} /> : <FiEye size={17} />}
              </button>
            </span>
          </div>
          <div className="pi-account-modal__field">
            <span id="new-password-label">New password</span>
            <span className="pi-account-modal__control">
              <input
                type={showPassword.newpassword ? "text" : "password"}
                name="newpassword"
                autoComplete="new-password"
                value={passwordForm.newpassword}
                onChange={(e) =>
                  setPasswordForm((prev) => ({
                    ...prev,
                    newpassword: e.target.value,
                  }))
                }
                aria-labelledby="new-password-label"
                required
              />
              <button
                type="button"
                className="pi-account-modal__toggle-password"
                onClick={() => toggleVisibility("newpassword")}
                aria-label={
                  showPassword.newpassword ? "Hide new password" : "Show new password"
                }
                aria-pressed={showPassword.newpassword}
              >
                {showPassword.newpassword ? <FiEyeOff size={17} /> : <FiEye size={17} />}
              </button>
            </span>
          </div>
          <div className="pi-account-modal__field">
            <span id="confirm-password-label">Confirm new password</span>
            <span className="pi-account-modal__control">
              <input
                type={showPassword.confirmpassword ? "text" : "password"}
                name="confirmpassword"
                autoComplete="new-password"
                value={passwordForm.confirmpassword}
                onChange={(e) =>
                  setPasswordForm((prev) => ({
                    ...prev,
                    confirmpassword: e.target.value,
                  }))
                }
                aria-labelledby="confirm-password-label"
                required
              />
              <button
                type="button"
                className="pi-account-modal__toggle-password"
                onClick={() => toggleVisibility("confirmpassword")}
                aria-label={
                  showPassword.confirmpassword
                    ? "Hide confirm password"
                    : "Show confirm password"
                }
                aria-pressed={showPassword.confirmpassword}
              >
                {showPassword.confirmpassword ? (
                  <FiEyeOff size={17} />
                ) : (
                  <FiEye size={17} />
                )}
              </button>
            </span>
          </div>
          <button type="submit" className="pi-account-modal__submit" disabled={saving}>
            {saving ? "Updating…" : "Update password"}
          </button>
        </form>
      );
    }

    return (
      <form className="pi-account-modal__form" onSubmit={saveProfile}>
        <div className="pi-account-modal__avatar-row">
          <span className="pi-account-modal__avatar">
            {imagePreview && !avatarFailed ? (
              <img
                src={imagePreview}
                alt=""
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              getAuthInitials(auth)
            )}
          </span>
          <div>
            <p className="pi-account-modal__note" style={{ marginBottom: "0.45rem" }}>
              {getAuthDisplayName(auth)}
            </p>
            <div className="pi-account-modal__avatar-actions">
              <label className="pi-account-modal__ghost-btn">
                {imageUploading ? "Updating…" : "Change photo"}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  disabled={imageUploading || saving}
                  onChange={onPickImage}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="pi-account-modal__row">
          <label className="pi-account-modal__field">
            <span>First name</span>
            <input
              name="first_name"
              value={profileForm.first_name}
              onChange={onProfileChange}
            />
          </label>
          <label className="pi-account-modal__field">
            <span>Last name</span>
            <input
              name="last_name"
              value={profileForm.last_name}
              onChange={onProfileChange}
            />
          </label>
        </div>

        <label className="pi-account-modal__field">
          <span>Username <em>(can&apos;t be changed)</em></span>
          <input
            value={identity.username}
            readOnly
            disabled
            className="pi-account-modal__input--locked"
          />
        </label>
        <label className="pi-account-modal__field">
          <span>Email <em>(can&apos;t be changed)</em></span>
          <input
            type="email"
            value={identity.email}
            readOnly
            disabled
            className="pi-account-modal__input--locked"
          />
        </label>

        {isDealer ? (
          <>
            <label className="pi-account-modal__field">
              <span>Company name</span>
              <input
                name="company_name"
                value={profileForm.company_name || ""}
                onChange={onProfileChange}
              />
            </label>
            <label className="pi-account-modal__field">
              <span>Mobile number</span>
              <PhoneWithCountryCode
                id="account-mobile-dealer"
                dial={profileForm.phone_dial}
                national={profileForm.phone_national}
                countryName={profileForm.country}
                onDialChange={onPhoneDialChange}
                onNationalChange={onPhoneNationalChange}
                disabled={saving || imageUploading}
              />
            </label>
            <label className="pi-account-modal__field">
              <span>Address</span>
              <input
                name="address"
                value={profileForm.address || ""}
                onChange={onProfileChange}
                placeholder="Locality/House/Street no."
              />
            </label>
            <div className="pi-account-modal__row">
              <label className="pi-account-modal__field">
                <span>City</span>
                <input
                  name="city"
                  value={profileForm.city || ""}
                  onChange={onProfileChange}
                />
              </label>
              <label className="pi-account-modal__field">
                <span>County</span>
                <select
                  name="county"
                  value={profileForm.county || ""}
                  onChange={onProfileChange}
                >
                  <option value="">Select county</option>
                  {locationLists.county.map((item) => (
                    <option key={item.id} value={String(item.id)}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="pi-account-modal__row">
              <label className="pi-account-modal__field">
                <span>Country</span>
                <CountrySelect
                  id="account-country-dealer"
                  name="country"
                  value={profileForm.country}
                  onChange={onCountryChange}
                  disabled={saving || imageUploading}
                />
              </label>
              <label className="pi-account-modal__field">
                <span>Postcode</span>
                <input
                  name="postcode"
                  value={profileForm.postcode || ""}
                  onChange={onProfileChange}
                />
              </label>
            </div>
            <label className="pi-account-modal__field">
              <span>Garage code</span>
              <input
                name="garagecode"
                value={profileForm.garagecode || ""}
                onChange={onProfileChange}
              />
            </label>
            <div className="pi-account-modal__row">
              <label className="pi-account-modal__field">
                <span>Business type</span>
                <select
                  name="business_type"
                  value={profileForm.business_type || "1"}
                  onChange={onProfileChange}
                >
                  {BUSINESS_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="pi-account-modal__field">
                <span>Next Gear Capital client</span>
                <select
                  name="client_type"
                  value={profileForm.client_type || "2"}
                  onChange={onProfileChange}
                >
                  {NEXT_GEAR_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </>
        ) : (
          <>
            <label className="pi-account-modal__field">
              <span>Mobile number</span>
              <PhoneWithCountryCode
                id="account-mobile"
                dial={profileForm.phone_dial}
                national={profileForm.phone_national}
                countryName={profileForm.country}
                onDialChange={onPhoneDialChange}
                onNationalChange={onPhoneNationalChange}
                disabled={saving || imageUploading}
              />
            </label>
            <label className="pi-account-modal__field">
              <span>Address</span>
              <input
                name="address"
                value={profileForm.address}
                onChange={onProfileChange}
              />
            </label>
            <div className="pi-account-modal__row">
              <label className="pi-account-modal__field">
                <span>Country</span>
                <CountrySelect
                  id="account-country"
                  name="country"
                  value={profileForm.country}
                  onChange={onCountryChange}
                  disabled={saving || imageUploading}
                />
              </label>
              <label className="pi-account-modal__field">
                <span>Postcode</span>
                <input
                  name="postcode"
                  value={profileForm.postcode}
                  onChange={onProfileChange}
                />
              </label>
            </div>
          </>
        )}

        <button
          type="submit"
          className="pi-account-modal__submit"
          disabled={saving || imageUploading}
        >
          {saving ? "Saving…" : "Save profile"}
        </button>
      </form>
    );
  };

  return (
    <Modal
      show={open}
      onHide={onClose}
      centered
      className="pi-account-modal"
      dialogClassName={
        activePanel === "profile" && isDealer
          ? "pi-account-modal__dialog pi-account-modal__dialog--dealer"
          : activePanel === "profile" ||
              activePanel === "credits" ||
              activePanel === "referral"
            ? "pi-account-modal__dialog pi-account-modal__dialog--wide"
            : "pi-account-modal__dialog"
      }
      aria-labelledby="account-quick-modal-title"
    >
      <div className="pi-account-modal__body">
        <div className="pi-account-modal__header">
          <div>
            <p className="pi-account-modal__eyebrow">{meta.eyebrow}</p>
            <h2 id="account-quick-modal-title" className="pi-account-modal__title">
              {meta.title}
            </h2>
            {meta.lead ? (
              <p className="pi-account-modal__lead">{meta.lead}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="pi-account-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="pi-account-modal__scroll">
          {message ? (
            <p
              className={`pi-account-modal__alert pi-account-modal__alert--${
                message.type === "ok" ? "ok" : "err"
              }`}
            >
              {message.text}
            </p>
          ) : null}

          {renderBody()}
        </div>
      </div>
    </Modal>
  );
}

const mapStateToProps = (state) => ({ auth: state.auth });
export default connect(mapStateToProps)(AccountQuickModal);
